import { Box, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { ALPHA, DISABLED_CONTROL, FONT_SIZE, GRID_RADIUS, INK, MOTION, NEUTRAL } from "@/theme/tokens";
import { FilterActiveDot } from "@/ui/Badges/FilterActiveDot";
import { rowHeightsFor } from "./DropdownRow";
import type { DropdownMenuAction } from "./types";

type Props = {
	actions: DropdownMenuAction[];
	/** Closes the menu before the chosen action runs, so a dialog it opens isn't covered by the menu. */
	onPicked: () => void;
};

/** A menu's commands under its options ("New report…"), ruled off from them, each a row of the menu's own size. */
export const DropdownMenuActions: FC<Props> = ({ actions, onPicked }) => {
	const isCoarsePointer = useMediaQuery("(pointer: coarse)");
	const rowHeight = rowHeightsFor(isCoarsePointer, "menu").single;
	return (
		<Box sx={{ flexShrink: 0, borderTop: `1px solid ${NEUTRAL[200]}`, mt: 0.75, pt: 0.75 }}>
			{actions.map((action) => (
				<Box
					key={action.dataTestId}
					component="button"
					type="button"
					disabled={action.disabled}
					data-testid={action.dataTestId}
					onClick={() => {
						onPicked();
						action.onSelect();
					}}
					sx={(theme) => ({
						display: "flex",
						alignItems: "center",
						width: "100%",
						height: rowHeight,
						px: 1.25,
						border: 0,
						borderRadius: GRID_RADIUS.CHIP,
						backgroundColor: "transparent",
						font: "inherit",
						fontSize: FONT_SIZE.EMPHASIS,
						color: INK.PRIMARY,
						textAlign: "start",
						cursor: "pointer",
						transition: `background-color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { transition: "none" },
						"@media (hover: hover) and (pointer: fine)": { "&:hover": { backgroundColor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY) } },
						"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
						"&:disabled": { cursor: "not-allowed", opacity: DISABLED_CONTROL.DIMMED_OPACITY },
					})}
				>
					{action.attention === undefined ? (
						action.label
					) : (
						<FilterActiveDot active label={action.attention} placement="text" size="large" dataTestId={`${action.dataTestId}-dot`}>
							{action.label}
						</FilterActiveDot>
					)}
				</Box>
			))}
		</Box>
	);
};
